import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, fonts, radius, space, shadow, text } from './theme';
import { Lockup } from './Logo';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${colors.surface};
  font-family: ${fonts.body};
  color: ${colors.onSurface};
  padding: ${space.lg};
  position: relative;
  background-image:
    radial-gradient(${colors.border} 1px, transparent 1px);
  background-size: 24px 24px;
  background-position: 0 0;

  @media (max-width: 600px) {
    padding: ${space.md} ${space.sm};
    justify-content: flex-start;
    padding-top: ${space.lg};
  }
`;

const Hero = styled.div`
  text-align: center;
  margin-bottom: ${space.xl};

  @media (max-width: 600px) {
    margin-bottom: ${space.lg};
  }
`;

const Tagline = styled.h1`
  ${text.displayLg};
  margin: ${space.lg} 0 ${space.sm};
  color: ${colors.onSurface};

  @media (max-width: 600px) {
    font-size: 22px;
    line-height: 1.25;
    margin: ${space.md} 0 ${space.xs};
    padding: 0 ${space.xs};
  }
`;

const Lede = styled.p`
  ${text.bodyMd};
  color: ${colors.onSurfaceMuted};
  max-width: 480px;
  margin: 0 auto;

  @media (max-width: 600px) {
    font-size: 14px;
    padding: 0 ${space.xs};
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: ${colors.surfaceElevated};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  padding: ${space.xl};
  box-shadow: ${shadow.lg};

  @media (max-width: 600px) {
    padding: ${space.lg} ${space.md};
    border-radius: ${radius.md};
  }
`;

const CardHeading = styled.h2`
  ${text.displayMd};
  margin: 0 0 ${space.xs};
  color: ${colors.onSurface};
`;

const CardSub = styled.p`
  ${text.bodySm};
  color: ${colors.onSurfaceMuted};
  margin: 0 0 ${space.lg};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${space.md};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
`;

const Label = styled.label`
  ${text.labelSm};
  color: ${colors.onSurfaceMuted};
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const Input = styled.input`
  ${text.bodyMd};
  padding: 11px 14px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.md};
  background: ${colors.surfaceElevated};
  color: ${colors.onSurface};
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: ${fonts.body};

  /* Prevent iOS auto-zoom when focusing — Safari zooms in unless the input
     font-size is >= 16px in the rendered (mobile) styles. */
  @media (max-width: 600px) {
    font-size: 16px;
    padding: 13px 14px;
  }

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: ${shadow.focus};
  }

  &::placeholder {
    color: ${colors.onSurfaceFaint};
  }
`;

const PrimaryButton = styled.button`
  ${text.bodyMd};
  font-weight: 600;
  padding: 12px 16px;
  background: ${colors.primary};
  color: #fff;
  border: 1px solid ${colors.primary};
  border-radius: ${radius.md};
  cursor: pointer;
  transition: background-color 0.15s;
  font-family: ${fonts.body};

  &:hover:not(:disabled) {
    background: ${colors.primaryHover};
    border-color: ${colors.primaryHover};
  }

  &:active:not(:disabled) {
    background: ${colors.primaryActive};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  ${text.bodyMd};
  font-weight: 500;
  padding: 11px 16px;
  background: transparent;
  color: ${colors.onSurface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.md};
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
  font-family: ${fonts.body};

  &:hover:not(:disabled) {
    background: ${colors.surfaceContainer};
    border-color: ${colors.borderStrong};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${colors.accent};
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: 13px;
  font-weight: 500;
  padding: 0;

  &:hover {
    color: ${colors.accentHover};
    text-decoration: underline;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.md};
  margin: ${space.lg} 0;
  color: ${colors.onSurfaceFaint};
  ${text.labelSm};

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${colors.border};
  }
`;

const Notice = styled.div`
  ${text.bodySm};
  padding: ${space.md};
  border-radius: ${radius.md};
  border: 1px solid ${props => props.$tone === 'error' ? colors.danger + '44' : colors.success + '44'};
  background: ${props => props.$tone === 'error' ? colors.dangerBg : colors.successBg};
  color: ${props => props.$tone === 'error' ? colors.danger : colors.success};
`;

const Footer = styled.div`
  position: absolute;
  bottom: ${space.lg};
  left: ${space.lg};
  right: ${space.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${colors.onSurfaceFaint};
  ${text.labelSm};

  /* On mobile, the footer steals space the card needs — restack as static
     content under the card so it doesn't overlap on short viewports. */
  @media (max-width: 600px) {
    position: static;
    margin-top: ${space.xl};
    flex-direction: column;
    gap: 4px;
    align-items: center;
    text-align: center;
  }
`;

function readResetTokenFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('reset');
}

function clearResetTokenFromUrl() {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  url.searchParams.delete('reset');
  window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
}

export default function Login({ onLogin }) {
  // Modes: 'login' | 'register' | 'forgot' | 'reset'
  // 'reset' is entered automatically when the URL has ?reset=<token>; everything
  // else is chosen by the user via the "Forgot?" / "Request access" links.
  const initialResetToken = readResetTokenFromUrl();
  const [mode, setMode] = useState(initialResetToken ? 'reset' : 'login');
  const [resetToken, setResetToken] = useState(initialResetToken || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // If we entered reset mode from a URL param, strip the token out of the
  // address bar so it doesn't leak via browser history / shoulder-surfing /
  // accidentally-shared screenshots.
  useEffect(() => {
    if (initialResetToken) clearResetTokenFromUrl();
  }, [initialResetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'forgot') {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/auth/reset-request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await response.json();
        if (response.ok) {
          setSuccess(data.message || 'If that email is registered, a reset link is on the way.');
          setEmail('');
        } else {
          setError(data.error || 'Could not request reset. Try again.');
        }
        return;
      }

      if (mode === 'reset') {
        if (password !== confirmPassword) {
          setError('Passwords do not match.');
          return;
        }
        if (password.length < 8) {
          setError('Password must be at least 8 characters.');
          return;
        }
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/auth/reset-confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetToken, password }),
        });
        const data = await response.json();
        if (response.ok) {
          setSuccess(data.message || 'Password updated. Sign in with your new password.');
          setPassword('');
          setConfirmPassword('');
          setResetToken('');
          setTimeout(() => {
            setMode('login');
            setSuccess('');
          }, 2500);
        } else {
          setError(data.error || 'Reset failed. The link may be expired — request a new one.');
        }
        return;
      }

      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = mode === 'login' ? { email, password } : { email, password, name };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        if (mode === 'login') {
          localStorage.setItem('bd_auth_token', data.token);
          onLogin(data.token);
        } else {
          setSuccess(data.message || 'Registration submitted. Your account is pending admin approval.');
          setEmail('');
          setPassword('');
          setName('');
          setTimeout(() => {
            setMode('login');
            setSuccess('');
          }, 4000);
        }
      } else {
        setError(data.error || 'Something went wrong.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === 'login';
  const isRegister = mode === 'register';
  const isForgot = mode === 'forgot';
  const isReset = mode === 'reset';

  const formValid =
    (isLogin && email && password) ||
    (isRegister && email && password && name) ||
    (isForgot && email) ||
    (isReset && password && confirmPassword);

  const heading = isLogin ? 'Sign in'
    : isRegister ? 'Request access'
    : isForgot ? 'Reset your password'
    : 'Set a new password';

  const sub = isLogin ? 'Enter your credentials to access the dashboard.'
    : isRegister ? 'New accounts require admin approval before sign-in.'
    : isForgot ? "Enter your email and we'll send you a link to set a new password."
    : 'Choose a new password for your account. Must be at least 8 characters.';

  const submitLabel = loading
    ? (isLogin ? 'Signing in…'
        : isRegister ? 'Submitting…'
        : isForgot ? 'Sending…'
        : 'Saving…')
    : (isLogin ? 'Sign in'
        : isRegister ? 'Submit request'
        : isForgot ? 'Send reset link'
        : 'Update password');

  const switchToLogin = () => {
    setMode('login');
    setError('');
    setSuccess('');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <Page>
      <Hero>
        <Lockup iconSize={36} wordSize={26} />
        <Tagline>Federal contracting intelligence, scored for you.</Tagline>
        <Lede>
          Crawl SAM.gov daily, score every opportunity against your company profile, and surface the ones worth pursuing.
        </Lede>
      </Hero>

      <Card>
        <CardHeading>{heading}</CardHeading>
        <CardSub>{sub}</CardSub>

        <Form onSubmit={handleSubmit}>
          {isRegister && (
            <Field>
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </Field>
          )}

          {!isReset && (
            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus={isLogin || isForgot}
              />
            </Field>
          )}

          {(isLogin || isRegister) && (
            <Field>
              <Label htmlFor="password">
                Password
                {isLogin && (
                  <LinkButton
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setError('');
                      setSuccess('');
                      setPassword('');
                    }}
                  >
                    Forgot?
                  </LinkButton>
                )}
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}

          {isReset && (
            <>
              <Field>
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                />
              </Field>
              <Field>
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Type it again"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </Field>
            </>
          )}

          <PrimaryButton type="submit" disabled={loading || !formValid}>
            {submitLabel}
          </PrimaryButton>

          {error && <Notice $tone="error">{error}</Notice>}
          {success && <Notice $tone="success">{success}</Notice>}

          {/* Reset flow always deadends silently if the email has no account
              (by design — don't leak which emails are registered). That leaves
              users staring at 'check your inbox' when nothing arrives. This
              hint gives them the correct path without revealing whether their
              specific email exists. */}
          {isForgot && success && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, lineHeight: 1.55, color: 'var(--text-1)' }}>
              <strong style={{ color: 'var(--text-0)' }}>Nothing in your inbox after a few minutes?</strong><br />
              Two most common reasons:
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                <li>You don't have an account yet. Use <em>Request access</em> below to sign up — an admin has to approve you before you can sign in.</li>
                <li>Your account exists but hasn't been approved yet. Reach out to your admin.</li>
              </ul>
            </div>
          )}
        </Form>

        {!isReset && (
          <>
            <Divider>
              {isLogin ? "Don't have an account?"
                : isRegister ? 'Already have access?'
                : 'Remembered it?'}
            </Divider>

            <GhostButton
              type="button"
              onClick={() => {
                if (isLogin) {
                  setMode('register');
                } else if (isForgot) {
                  setMode('register');
                  setError('');
                  setSuccess('');
                  return;
                } else {
                  switchToLogin();
                }
                setError('');
                setSuccess('');
              }}
            >
              {isLogin ? 'Request access' : isRegister ? 'Back to sign in' : isForgot ? 'Request access' : 'Back to sign in'}
            </GhostButton>
            {isForgot && (
              <button
                type="button"
                onClick={switchToLogin}
                style={{ display: 'block', margin: '10px auto 0', background: 'none', border: 'none', color: 'var(--text-2)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Back to sign in
              </button>
            )}
          </>
        )}

        {isReset && (
          <>
            <Divider>Or</Divider>
            <GhostButton type="button" onClick={switchToLogin}>
              Back to sign in
            </GhostButton>
          </>
        )}
      </Card>

      <Footer>
        <span>© {new Date().getFullYear()} SAMKLR</span>
        <span>Federal procurement intelligence</span>
      </Footer>
    </Page>
  );
}
